import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  Typography,
  Button,
  Box,
  Avatar,
  Chip,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  Business as BusinessIcon,
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { SwalConfig } from '../../../utils/swalConfig';

const ViewClient = () => {
  const { id } = useParams();
  const [clientData, setClientData] = useState(null);
  const [buData, setBuData] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Fetch Business Units
  const fetchBusinessUnits = async () => {
    try {
      const response = await fetch('https://api-sso-v2.eurekagroup.id/bu', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      setBuData(result);
      console.log('Business units loaded:', result);
    } catch (error) {
      console.error('Error fetching business units:', error);
    }
  };

  // Fetch Client Data
  const fetchClientData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`https://api-sso-v2.eurekagroup.id/api/clients/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      const data = result.data || result;
      setClientData(data);
      console.log('Client data loaded:', data);
    } catch (error) {
      console.error('Error fetching client data:', error);
      Swal.fire({
        ...SwalConfig.error,
        title: 'Error!',
        text: `Gagal memuat data client: ${error.message}`
      });
      navigate('/sso/client');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBusinessUnits();
    fetchClientData();
  }, [id]);

  const getBuName = (idBu) => {
    const bu = buData.find(item => item.bu_code === idBu.toString());
    return bu ? bu.bu_name : `BU ${idBu}`;
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return 'primary';
      case 'superadmin':
        return 'error';
      case 'user':
        return 'success';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: 400,
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ color: 'text.secondary' }}>
          Memuat data client...
        </Typography>
      </Box>
    );
  }

  if (!clientData) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" sx={{ color: 'text.secondary' }}>
          Data client tidak ditemukan
        </Typography>
      </Box>
    );
  }

  return (
    <div className="animate-fade-in">
      <Card sx={{ boxShadow: '0 8px 32px rgba(0,0,0,0.1)', borderRadius: 3 }}>
        <CardHeader 
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }} className="shadow-lg">
                <BusinessIcon />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }} className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Detail Client SSO
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} className="text-gray-600">
                  Informasi lengkap client SSO
                </Typography>
              </Box>
            </Box>
          }
          action={
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/sso/client')}
                sx={{
                  borderRadius: 2,
                  px: 2,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Kembali
              </Button>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/sso/client/edit/${id}`)}
                className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                Edit Client
              </Button>
            </Box>
          }
          sx={{ pb: 2 }}
        />
        <Divider />
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                p: 3, 
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200'
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                  Informasi Client
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                      Nama Client
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      {clientData.client}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                      Client ID
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace', bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
                      {clientData._id}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                p: 3, 
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200'
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                  Konfigurasi
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                      Business Unit
                    </Typography>
                    <Chip 
                      label={getBuName(clientData.id_bu)}
                      color="info"
                      variant="outlined"
                      sx={{ fontWeight: 500 }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                      Role
                    </Typography>
                    <Chip 
                      label={clientData.role}
                      color={getRoleColor(clientData.role)}
                      sx={{ fontWeight: 500 }}
                    />
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </div>
  );
};

export default ViewClient;
